<template>
  <div class="modal">
    <div class="modal-content">
      <h3><strong>{{ $t('addlist') }}</strong></h3><br>
      <form @submit.prevent="saveEvent">
        <div>
          <div
            v-if="isLoading && isLoadingDevice"
            class="loading-spinner"
          >
            <b-spinner label="Loading..." />
          </div>
          <div v-else>
            <!-- Event Title -->
            <h5>{{ $t('title') }}: <input
              v-model="event.title"
              maxlength="50"
              type="text"
              required
            ></h5>

            <h5>{{ $t('details') }}:<input
              v-model="event.description"
              maxlength="100"
              type="text"
              required
            ></h5>

            <!-- Start Time -->
            <h5>{{ $t('starttime') }}: <input
              v-model="event.startDateTime"
              type="datetime-local"
              required
            ></h5>

            <!-- End Time -->
            <h5>{{ $t('endtime') }}:<input
              v-model="event.endDateTime"
              type="datetime-local"
              :min="event.startDateTime"
              required
            ></h5>

            <b-form-select
              v-if="!isLoading"
              v-model="event.deviceId"
              :select-size="4"
              required
              @change="onDeviceChange"
            >
              <b-form-select-option
                v-for="(option, index) in formattedOptions"
                :key="`${option.value}-${index}`"
                :value="option.value"
                :class="{
                  'category-option': option.isCategory,
                  'subcategory-option': option.isSubcategory,
                  'device-option': option.isDevice
                }"
                :disabled="option.isCategory || option.isSubcategory"
              >
                {{ option.text }}
              </b-form-select-option>
            </b-form-select>

            <!-- Buttons -->
            <div class="modal-buttons">
              <b-button
                type="button"
                variant="secondary"
                @click="close"
              >
                {{ $t('cancel') }}
              </b-button>
              <b-button
                type="submit"
                variant="primary"
              >
                {{ $t('submit') }}
              </b-button>
            </div>
          </div>
        </div>
      </form>
    </div>
    <div
      v-if="isLoadingsave"
      class="overlay"
    >
      <div class="loading-container">
        <b-spinner
          label="Loading..."
          class="spinner"
        />
        <p class="loading-text">
          {{ $t('loading') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import apiMst from '@/services/masterDate'
import apiAuth from '@/services/auth'
import emailservice from '@/services/emailservice'

export default {
  props: ['date'],
  data() {
    return {
      event: {
        title: '',
        description: '',
        startDateTime: this.formatDateTime(this.date),
        endDateTime: this.formatDateTime(this.date),
        deviceCategoryId: null,
        deviceSubcategoryId: null,
        deviceId: null,
      },
      deviceCategoriesOptions: [],
      deviceSubcategoriesOptions: [],
      devicesOptions: [],
      formattedOptions: [],
      isLoading: false,
      isLoadingDevice: false,
      isLoadingDeviceCate: false,
      isLoadingsave: false,
    }
  },

  mounted() {
    Promise.all([
      this.fetchCategories(),
      this.fetchDevices(),
    ])
  },
  methods: {
    formatDateTime(date) {
      const dateObj = new Date(date)
      return dateObj.toISOString().slice(0, 16)
    },

    async fetchCategories() {
      this.isLoading = true
      try {
        const response = await apiMst.get('/device')
        this.deviceCategoriesOptions = response.data.map(category => ({
          value: category.id,
          text: category.name,
          subcate: category.deviceSubCate || [],
        }))
      } catch (error) {
        console.error('Error fetching device categories:', error)
        this.deviceCategoriesOptions = []
      } finally {
        this.isLoading = false
      }
    },

    async fetchSubcategories() {
      if (!this.event.deviceCategoryId) return
      this.isLoadingDeviceCate = true
      try {
        const response = await apiMst.get(`/subdevice/${this.event.deviceCategoryId}`)

        if (Array.isArray(response.data) && response.data.length > 0) {
          this.deviceSubcategoriesOptions = response.data.map(subcat => ({
            value: subcat.id,
            text: subcat.name,
          }))
        } else {
          this.deviceSubcategoriesOptions = []
          console.warn('ไม่พบหมวดหมู่ย่อยของอุปกรณ์')

          this.$bvToast.toast(this.$t('notfoundsubcateIndevice'), {
            title: this.$t('warning'),
            variant: 'warning',
            solid: true,
          })
        }
      } catch (error) {
        console.error('Error fetching device subcategories:', error)
        this.deviceSubcategoriesOptions = []

        this.$bvToast.toast(this.$t('getsubcatedevicefailed'), {
          title: this.$t('error'),
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isLoadingDeviceCate = false
      }
    },

    async fetchDevices() {
      this.isLoadingDevice = true
      try {
        const response = await apiMst.get('/deviceListAll')
        if (response.data) {
          const deviceData = response.data
          this.devicesOptions = deviceData.map(device => ({
            value: device.id,
            text: device.deviceName || device.name,
            categoryId: device.categoryId || device.category?.id || device.deviceCategoryId,
            subcategoryId: device.subcategoryId || device.subcategory?.id || device.deviceSubcategoryId,
          }))
          this.formatOptions()
        } else {
          console.warn('No devices found or unexpected response format')
          this.devicesOptions = []
        }
      } catch (error) {
        console.error('Error fetching devices:', error)
        this.devicesOptions = []
      } finally {
        this.isLoadingDevice = true
      }
    },

    formatOptions() {
      this.formattedOptions = []
      if (this.deviceCategoriesOptions.length === 0 || this.devicesOptions.length === 0) {
        return
      }

      this.deviceCategoriesOptions.forEach(category => {
        this.formattedOptions.push({
          value: category.value,
          text: category.text,
          isCategory: true,
        })

        if (category.subcate && category.subcate.length > 0) {
          const sortedSubcategories = category.subcate.sort((a, b) => a.name.localeCompare(b.name))

          sortedSubcategories.forEach(subcategory => {
            this.formattedOptions.push({
              value: subcategory.id,
              text: `${subcategory.name}`,
              isSubcategory: true,
              categoryId: category.value,
            })

            const subcategoryDevices = this.devicesOptions.filter(device => device.subcategoryId === subcategory.id)
            const sortedDevices = subcategoryDevices.sort((a, b) => a.text.localeCompare(b.text))

            sortedDevices.forEach(device => {
              this.formattedOptions.push({
                value: device.value,
                text: `>>> ${device.text}`,
                isDevice: true,
                categoryId: device.categoryId,
                subcategoryId: device.subcategoryId,
              })
            })
          })
        }
      })

      this.formattedOptions.sort((a, b) => {
        if (a.isCategory && b.isCategory) {
          return a.text.localeCompare(b.text)
        } if (a.isSubcategory && b.isSubcategory) {
          return a.text.localeCompare(b.text)
        } if (a.isDevice && b.isDevice) {
          return a.text.localeCompare(b.text)
        }
        return 0
      })

      console.log('Formatted Options:', this.formattedOptions)
    },

    onDeviceChange() {
      const selectedOption = this.formattedOptions.find(
        option => option.value === this.event.deviceId && option.isDevice,
      )

      if (selectedOption) {
        this.event.deviceId = selectedOption.value
        this.event.deviceSubcategoryId = selectedOption.subcategoryId
        this.event.deviceCategoryId = selectedOption.categoryId
      }
    },

    async fetchEmails() {
      try {
        const { deviceId } = this.event
        if (!deviceId) {
          throw new Error('Device ID is required')
        }
        const response = await apiAuth.get('/email/searchemailBydeviceId', {
          params: { deviceId },
        })

        if (response.status === 200) {
          const emails = response.data
          if (emails && Array.isArray(emails) && emails.length > 0) {
            const validEmails = emails.filter(email => email && typeof email === 'string' && email.trim().length > 0)
            if (validEmails.length > 0) {
              return validEmails.map(email => email.trim())
            }
            throw new Error('No valid emails found in API response')
          } else {
            throw new Error('No emails found in API response')
          }
        }
        throw new Error('Failed to fetch worker emails')
      } catch (error) {
        console.error('Error fetching worker emails:', error)
        throw error
      }
    },

    async sendEmailNotification(emailData) {
      try {
        if (!emailData || emailData.length === 0) {
          throw new Error('Recipients list is empty')
        }

        const isTokenValid = emailservice.isTokenValid()
        if (!isTokenValid) {
          throw new Error('Please log in again to send emails')
        }
        const emailTemplate = `
      <!DOCTYPE html>
      <html>
      <body>
        <p>เรียน <strong>ผู้ใช้งานอุปกรณ์ ${this.getDeviceNameById(this.event.deviceId) ?? 'N/A'}</strong>,</p>
        <p>แจ้งรายละเอียดตรวจเช็คสภาพอุปกรณ์(PM) มีรายละเอียดดังนี้:</p>
        <ul>
          <li>หัวข้อ: ${this.event.title ?? 'N/A'}</li>
          <li>เวลา: ${new Date(this.event.startDateTime).toLocaleDateString()} ${new Date(this.event.startDateTime).toLocaleTimeString()} - ${new Date(this.event.endDateTime).toLocaleDateString()} ${new Date(this.event.endDateTime).toLocaleTimeString()}</li>
          <li>ชื่ออุปกรณ์: ${this.getDeviceNameById(this.event.deviceId)}</li>
          <li>รายละเอียด: ${this.event.description}</li>
        </ul>
        <p>ขอแสดงความนับถือ</p>
      </body>
      </html>
    `

        await emailservice.sendEmailWithTemplate(emailData, emailTemplate, {
          subject: `ตรวจเช็คสภาพอุปกรณ์PM (${this.getDeviceNameById(this.event.deviceId) ?? 'N/A'})`,
        })
      } catch (error) {
        console.error('Error sending email:', error)
        throw error
      }
    },

    getDeviceNameById(id) {
      const device = this.devicesOptions.find(d => d.value === id)
      return device ? device.text : 'N/A'
    },

    async saveEvent() {
      this.isLoadingsave = true
      try {
        if (!this.event.title || !this.event.deviceCategoryId || !this.event.deviceSubcategoryId || !this.event.deviceId) {
          this.$bvToast.toast(this.$t('confirminput'), {
            title: this.$t('error'),
            variant: 'danger',
            solid: true,
          })
          return
        }

        const Emails = await this.fetchEmails(this.event.deviceId)
        if (!Emails || Emails.length === 0) {
          throw new Error('ไม่พบอีเมลผู้ดำเนินการซ่อม')
        }
        await this.sendEmailNotification(Emails, this.event)
        await this.$emit('save', this.event)
      } catch (error) {
        console.error('Error saving data:', error)
        alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล')
      } finally {
        this.isLoadingsave = false
      }
    },

    close() {
      this.$emit('close')
    },
  },
}
</script>

<style scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 10px;
  width: 400px;
}

.modal-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}

form label {
  display: block;
  margin-top: 10px;
}

input, select {
  width: 100%;
  padding: 8px;
  margin: 5px 0;
}

.category-option {
  background-color: #949494;
  color: white;
  font-weight: bold;
  font-size: 16px;
}
.subcategory-option{
  font-weight: bold;
  font-size: 14px;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
}

.loading-text {
  margin-top: 10px;
  font-size: 16px;
}
.b-spinner {
  color: white;
}
.loading-container {
  text-align: center;
  color: #fff;
}
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

</style>
